import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import Layout from '../components/Layout';
import OnboardingModal from '../components/OnboardingModal';
import TourGuide from '../components/TourGuide';
import { SkeletonCard } from '../components/Skeleton';
import { T } from '../styles/theme';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSubscription } from '../hooks/useSubscription';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart,
} from 'recharts';

function CustomTooltipLine({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: T.elem, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 14px', fontFamily: T.fontBody }}>
      <p style={{ color: T.muted, fontSize: '0.8rem', marginBottom: 4 }}>{label}</p>
      <p style={{ color: T.green, fontSize: '0.85rem' }}>Profitto: €{payload[0]?.value?.toFixed(2)}</p>
      {payload[1] && <p style={{ color: T.red, fontSize: '0.85rem' }}>Commissioni: €{payload[1]?.value?.toFixed(2)}</p>}
    </div>
  );
}

function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''; }

const SUBTITLES = {
  admin: 'Gestisci il tuo impero come un boss',
  pro: 'Stai sbloccando il tuo potenziale',
  free: 'Inizia il tuo percorso da reseller',
  collaboratore: 'Grazie per il tuo contributo',
};

function AnimatedNumber({ value = 0, duration = 1500 }) {
  const [display, setDisplay] = useState('0.00');
  const ref = useRef(null);
  useEffect(() => {
    const raw = typeof value === 'number' ? value : parseFloat(String(value || '0').replace(/[^0-9.-]/g, '')) || 0;
    const target = parseFloat(raw.toFixed(2));
    if (target === 0) { setDisplay('0.00'); return; }
    const increment = target / 60;
    let current = 0;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      current = progress * target;
      if (current >= target) { setDisplay(target.toFixed(2)); return; }
      setDisplay(current.toFixed(2));
      if (progress < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => { if (ref.current) cancelAnimationFrame(ref.current); };
  }, [value, duration]);
  return display;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [corsoProgress, setCorsoProgress] = useState(null);
  const [chartTab, setChartTab] = useState('30g');
  const [aggiornamenti, setAggiornamenti] = useState(null);

  const isMobile = useIsMobile();
  const { isPro, isAdmin } = useSubscription();

  useEffect(() => {
    Promise.all([
      api.get('/auth/me').then((r) => { setUser(r.data); if (!r.data.onboarding_completato) setShowOnboarding(true); else if (!localStorage.getItem('tour_completed')) setShowTour(true); }).catch(() => {}),
      api.get('/stats').then((r) => setStats(r.data)).catch(() => {}),
      api.get('/sales').then((r) => setRecentSales(Array.isArray(r.data) ? r.data.slice(0, 5) : [])).catch(() => {}),
      api.get('/stats/grafici').then((r) => setChartData(r.data)).catch(() => {}),
      api.get('/corso/progresso').then((r) => setCorsoProgress(r.data)).catch(() => {}),
      api.get('/aggiornamenti').then((r) => setAggiornamenti(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const ruolo = user?.ruolo || 'free';
  const hasProducts = (stats?.prodotti_disponibili || 0) + (stats?.prodotti_venduti || 0) > 0;
  const profittoTotale = stats?.profitto_totale || stats?.profitto_mese_corrente || 0;

  const giorniMap = { '7g': 7, '30g': 30, '90g': 90 };

  useEffect(() => {
    if (!loading) {
      api.get(`/stats/grafici?giorni=${giorniMap[chartTab]}`).then((r) => setChartData(r.data)).catch(() => {});
    }
  }, [chartTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const profittiFormatted = (chartData?.profitti || chartData?.profitti_30giorni || []).map((d) => ({
    ...d, label: d.data ? `${d.data.slice(8,10)}/${d.data.slice(5,7)}` : '',
    profitto: +(d.profitto || 0).toFixed(2), commissioni: +(d.commissioni || 0).toFixed(2),
  }));
  const hasSalesData = profittiFormatted.some((d) => d.profitto !== 0 || d.commissioni !== 0);

  const statCards = stats ? [
    { title: 'PRODOTTI', value: stats?.prodotti_disponibili || 0, color: T.violet, bgColor: 'rgba(167,139,250,0.15)' },
    { title: 'VENDUTI', value: stats?.prodotti_venduti || 0, color: T.green, bgColor: 'rgba(74,222,128,0.15)' },
    { title: 'VALORE', value: `€${(stats?.valore_inventario || 0).toFixed(0)}`, color: T.amber, bgColor: 'rgba(251,191,36,0.15)' },
    { title: 'CORSO', value: corsoProgress ? `${corsoProgress.completed || 0}/${corsoProgress.total || 19}` : '0/19', color: T.violet, bgColor: 'rgba(167,139,250,0.15)' },
  ] : [];

  return (
    <Layout>
      {showOnboarding && <OnboardingModal userName={capitalize(user?.name)} onComplete={() => { setShowOnboarding(false); if (!localStorage.getItem('tour_completed')) setShowTour(true); setLoading(true); Promise.all([api.get('/stats').then(r => setStats(r.data)).catch(() => {}), api.get('/sales').then(r => setRecentSales(Array.isArray(r.data) ? r.data.slice(0,5) : [])).catch(() => {})]).finally(() => setLoading(false)); }} />}
      {showTour && !showOnboarding && <TourGuide onComplete={() => setShowTour(false)} />}

      <div style={{ maxWidth: 1200, margin: '0 auto', paddingTop: isMobile ? '12px' : '32px', paddingLeft: isMobile ? 16 : 0, paddingRight: isMobile ? 16 : 0 }}>
        {/* HERO BANNER */}
        {!loading && hasProducts && (
          <div style={{
            background: T.gradHero, border: `1px solid ${T.border}`, borderRadius: 20,
            padding: isMobile ? 16 : 32, position: 'relative', overflow: 'hidden', marginBottom: 24,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24 }}>
              <div>
                <h1 style={{ fontFamily: T.fontDisplay, fontSize: isMobile ? '1.2rem' : '2rem', fontWeight: 800, color: T.text, marginBottom: 4 }}>
                  Ciao, {user ? capitalize(user.discord_username || user.name) : ''} 👋
                </h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <p style={{ fontFamily: T.fontBody, fontSize: '0.9rem', color: T.muted }}>{SUBTITLES[ruolo] || SUBTITLES.free}</p>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
                    fontFamily: T.fontBody, letterSpacing: '0.5px',
                    background: isPro ? 'rgba(74,222,128,0.15)' : 'rgba(107,114,128,0.15)',
                    color: isPro ? T.green : T.muted,
                    border: `1px solid ${isPro ? 'rgba(74,222,128,0.3)' : 'rgba(107,114,128,0.2)'}`,
                  }}>
                    {isPro ? '⚡ PRO' : '🆓 FREE'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 10, flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                  <Link to="/products" className="btn-primary" style={{ width: isMobile ? '100%' : 'auto', textAlign: 'center', justifyContent: 'center' }}>➕ Aggiungi Prodotto</Link>
                  <Link to="/analisi-mercato" className="btn-secondary" style={{ width: isMobile ? '100%' : 'auto', textAlign: 'center', justifyContent: 'center' }}>📊 Analizza</Link>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '1.6rem' : '3rem', fontWeight: 800, color: T.green, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  €<AnimatedNumber value={profittoTotale || 0} />
                </p>
                <p style={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.muted }}>Profitto Totale</p>
              </div>
            </div>
            {/* Decorative circle */}
            <div className="hero-decoration" style={{ position: 'absolute', right: -60, top: '50%', transform: 'translateY(-50%)', width: 240, height: 240, borderRadius: '50%', background: 'conic-gradient(#7c3aed,#4f46e5,#a78bfa,#7c3aed)', opacity: 0.07, animation: 'spin 20s linear infinite', display: isMobile ? 'none' : 'block' }} />
          </div>
        )}

        {/* ONBOARDING */}
        {!loading && !hasProducts && (
          <div style={{
            background: T.gradHero, border: `1px solid ${T.border}`, borderRadius: 20,
            padding: isMobile ? '20px 16px' : 32, marginBottom: 24, textAlign: 'center',
          }}>
            <h2 style={{ fontFamily: T.fontDisplay, fontSize: isMobile ? '1.3rem' : '1.8rem', fontWeight: 800, color: T.text, marginBottom: 6 }}>👋 Benvenuto in Impero Elite!</h2>
            <p style={{ fontFamily: T.fontDisplay, fontSize: isMobile ? '0.95rem' : '1.2rem', fontWeight: 700, marginBottom: isMobile ? 16 : 24, background: 'linear-gradient(135deg, #a78bfa, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Segui i 3 step per iniziare</p>
            <div style={{ display: 'flex', gap: isMobile ? 8 : 16, justifyContent: 'center', alignItems: 'center' }}>
              {[
                { step: 1, icon: '📦', title: 'Aggiungi prodotto', active: true, to: '/products' },
                { step: 2, icon: '📊', title: 'Analizza', active: false },
                { step: 3, icon: '🤖', title: 'Crea annuncio', active: false },
              ].map((s, i) => (
                <div key={s.step} style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 12, flex: isMobile ? 1 : 'none', minWidth: 0 }}>
                  {s.active ? (
                    <Link to={s.to} style={{
                      background: T.elem, border: `1px solid ${T.violetDark}`, borderRadius: isMobile ? 12 : 14,
                      padding: isMobile ? '14px 8px' : 20,
                      width: isMobile ? '100%' : 160, textAlign: 'center', textDecoration: 'none', transition: '0.15s',
                      boxShadow: '0 0 24px rgba(124,58,237,0.3)',
                    }}>
                      <span style={{ fontSize: isMobile ? '1.4rem' : '2rem', display: 'block', marginBottom: isMobile ? 4 : 8 }}>{s.icon}</span>
                      <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.72rem' : '0.9rem', fontWeight: 700, color: T.text, margin: 0 }}>{s.title}</p>
                      <span className="btn-primary" style={{ marginTop: isMobile ? 6 : 8, fontSize: isMobile ? '0.68rem' : '0.78rem', padding: isMobile ? '4px 10px' : '6px 14px', display: 'inline-block' }}>Inizia →</span>
                    </Link>
                  ) : (
                    <div style={{
                      background: T.elem, border: `1px solid ${T.border}`, borderRadius: isMobile ? 12 : 14,
                      padding: isMobile ? '14px 8px' : 20,
                      width: isMobile ? '100%' : 160, textAlign: 'center', opacity: 0.5,
                    }}>
                      <span style={{ fontSize: isMobile ? '1.4rem' : '2rem', display: 'block', marginBottom: isMobile ? 4 : 8 }}>{s.icon}</span>
                      <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.72rem' : '0.9rem', fontWeight: 700, color: T.text, margin: 0 }}>{s.title}</p>
                      <span style={{ fontSize: isMobile ? '0.65rem' : '0.72rem', color: T.muted }}>🔒 Step {s.step}</span>
                    </div>
                  )}
                  {i < 2 && <span style={{ color: T.violetDark, fontSize: isMobile ? '0.9rem' : '1.2rem', flexShrink: 0 }}>→</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STAT CARDS */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 8 : 16, marginBottom: 24 }} className="grid-cols-2 lg:grid-cols-4">
          {loading ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />) :
            statCards.map((card, idx) => {
              const numVal = typeof card.value === 'string' ? parseFloat(card.value.replace(/[^0-9.-]/g, '')) || 0 : (card.value || 0);
              return (
                <div key={card.title} className="card-gradient-border" style={{ animation: `fadeInUp 0.5s ease-out both`, animationDelay: `${idx * 0.1}s` }}>
                  <div className="card-gradient-border-inner" style={{ position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 8, right: 8, width: 40, height: 40, borderRadius: '50%', background: card.bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                    <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '1.4rem' : '2.2rem', fontWeight: 800, color: card.color, animation: `countUp 0.6s ease both`, animationDelay: `${idx * 0.1}s` }}>
                      {card.value}
                    </p>
                    <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.62rem' : '0.7rem', fontWeight: 600, letterSpacing: '0.1em', color: T.muted, textTransform: 'uppercase' }}>{card.title}</p>
                    <span style={{ fontSize: '0.78rem', color: numVal > 0 ? T.green : T.muted }}>{numVal > 0 ? `↑ questo mese` : '—'}</span>
                  </div>
                </div>
              );
            })}
        </div>

        {/* CHART */}
        {!loading && (
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontFamily: T.fontDisplay, fontSize: '1rem', fontWeight: 700, color: T.text }}>📈 Profitti</h3>
              <div style={{ display: 'flex', gap: 4 }}>
                {['7g', '30g', '90g'].map((tab) => (
                  <button key={tab} onClick={() => setChartTab(tab)} style={{
                    fontFamily: T.fontBody, fontSize: '0.8rem', padding: '4px 10px', borderRadius: 6,
                    background: chartTab === tab ? T.violetDark : 'transparent',
                    color: chartTab === tab ? 'white' : T.muted,
                    border: 'none', cursor: 'pointer',
                  }}>{tab}</button>
                ))}
              </div>
            </div>
            {hasSalesData ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={profittiFormatted}>
                  <defs>
                    <linearGradient id="violetGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(167,139,250,0.25)" />
                      <stop offset="100%" stopColor="rgba(167,139,250,0)" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={T.border} strokeDasharray="4 4" />
                  <XAxis dataKey="label" stroke="transparent" tick={{ fill: T.muted, fontSize: 11, fontFamily: T.fontBody }} />
                  <YAxis stroke="transparent" tick={{ fill: T.muted, fontSize: 11, fontFamily: T.fontBody }} />
                  <Tooltip content={<CustomTooltipLine />} />
                  <Area type="monotone" dataKey="profitto" stroke={T.violet} strokeWidth={2.5} fill="url(#violetGrad)" dot={false} activeDot={{ fill: T.violetDark, r: 5, strokeWidth: 0 }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.muted, fontSize: '0.9rem' }}>Nessuna vendita ancora</div>}
          </div>
        )}

        {/* BOTTOM GRID: Ultime Vendite + Azioni Rapide */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '3fr 2fr', gap: isMobile ? 12 : 16, marginTop: 24 }}>
          {/* Ultime Vendite */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ fontFamily: T.fontDisplay, fontSize: '1rem', fontWeight: 700, color: T.text }}>Ultime Vendite</h3>
              <Link to="/vendite" style={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.violet, textDecoration: 'none' }}>Vedi tutte →</Link>
            </div>
            {Array.isArray(recentSales) && recentSales.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: T.elem, borderBottom: `1px solid ${T.border}` }}>
                    <th style={{ fontFamily: T.fontBody, fontSize: '0.72rem', textTransform: 'uppercase', color: T.muted, padding: '10px 14px', textAlign: 'left' }}>Prodotto</th>
                    <th style={{ fontFamily: T.fontBody, fontSize: '0.72rem', textTransform: 'uppercase', color: T.muted, padding: '10px 14px', textAlign: 'left' }}>Piattaforma</th>
                    <th style={{ fontFamily: T.fontBody, fontSize: '0.72rem', textTransform: 'uppercase', color: T.muted, padding: '10px 14px', textAlign: 'right' }}>€</th>
                    <th style={{ fontFamily: T.fontBody, fontSize: '0.72rem', textTransform: 'uppercase', color: T.muted, padding: '10px 14px', textAlign: 'right', display: isMobile ? 'none' : 'table-cell' }}>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSales.map((s) => (
                    <tr key={s.id} style={{ borderBottom: `1px solid rgba(45,31,110,0.3)`, transition: '0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 14px', fontFamily: T.fontBody, fontSize: isMobile ? '0.78rem' : '0.88rem', color: T.text, maxWidth: isMobile ? 120 : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.product_name}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span className={s.piattaforma === 'eBay' ? 'badge badge-violet' : 'badge badge-amber'}>{s.piattaforma}</span>
                      </td>
                      <td style={{ padding: '10px 14px', fontFamily: T.fontBody, fontSize: isMobile ? '0.78rem' : '0.88rem', color: T.green, textAlign: 'right', fontWeight: 600 }}>€{(s.profitto_netto || 0).toFixed(2)}</td>
                      <td style={{ padding: '10px 14px', fontFamily: T.fontBody, fontSize: isMobile ? '0.72rem' : '0.82rem', color: T.muted, textAlign: 'right', display: isMobile ? 'none' : 'table-cell' }}>{new Date(s.data_vendita).toLocaleDateString('it-IT')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <span style={{ fontSize: '2rem' }}>📦</span>
                <p style={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.muted, marginTop: 8 }}>Nessuna vendita ancora</p>
              </div>
            )}
          </div>

          {/* Azioni Rapide */}
          <div className="card">
            <h3 style={{ fontFamily: T.fontDisplay, fontSize: '1rem', fontWeight: 700, color: T.text, marginBottom: 16 }}>Azioni Rapide</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Link to="/annunci/crea" style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '10px 12px' : '14px 16px', borderRadius: 16,
                background: T.gradPrimary, textDecoration: 'none', cursor: 'pointer', transition: '0.15s',
              }}>
                <span style={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>🤖</span>
                <div>
                  <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.82rem' : '0.88rem', fontWeight: 600, color: 'white' }}>Crea Annuncio AI</p>
                  <p style={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.violet }}>Titolo, desc, hashtag</p>
                </div>
              </Link>
              <Link to="/analisi-mercato" style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '10px 12px' : '14px 16px', borderRadius: 16,
                background: T.elem, border: `1px solid ${T.violetDark}`, textDecoration: 'none', cursor: 'pointer', transition: '0.15s',
              }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = T.shadowGlow}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}>
                <span style={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>📊</span>
                <div>
                  <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.82rem' : '0.88rem', fontWeight: 600, color: T.text }}>Analizza Mercato</p>
                </div>
              </Link>
              <Link to="/products" style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '10px 12px' : '14px 16px', borderRadius: 16,
                background: T.elem, border: `1px solid ${T.border}`, textDecoration: 'none', cursor: 'pointer', transition: '0.15s',
              }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = T.violet}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = T.border}>
                <span style={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>➕</span>
                <div>
                  <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.82rem' : '0.88rem', fontWeight: 600, color: T.text }}>Aggiungi Prodotto</p>
                </div>
              </Link>
              <Link to="/corso" style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '10px 12px' : '14px 16px', borderRadius: 16,
                background: T.elem, border: `1px solid ${T.border}`, textDecoration: 'none', cursor: 'pointer', transition: '0.15s',
              }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = T.violet}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = T.border}>
                <span style={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>🎓</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.82rem' : '0.88rem', fontWeight: 600, color: T.text }}>Continua Corso</p>
                  {corsoProgress && (
                    <div style={{ marginTop: 4, height: 4, borderRadius: 2, background: T.border, width: '100%' }}>
                      <div style={{ height: '100%', borderRadius: 2, background: T.green, width: `${corsoProgress.percent}%` }} />
                    </div>
                  )}
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* ULTIMI AGGIORNAMENTI — in fondo, caricati dal backend */}
        {!loading && aggiornamenti && aggiornamenti.items?.length > 0 && (
          <div className="card" style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontFamily: T.fontDisplay, fontSize: '1rem', fontWeight: 700, color: T.text }}>🚀 Ultimi Aggiornamenti</h3>
              <span style={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.muted, padding: '3px 10px', borderRadius: 20, background: 'rgba(167,139,250,0.1)', border: `1px solid rgba(167,139,250,0.2)` }}>v{aggiornamenti.versione}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {aggiornamenti.items.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', borderRadius: 12,
                  background: T.elem, border: `1px solid ${T.border}`,
                  transition: '0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = T.violet + '60'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = T.border}>
                  <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{item.icon || '✨'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, color: T.text, margin: 0 }}>{item.title}</p>
                    <p style={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.muted, margin: 0 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
